import type { FC } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@ui/dialog.tsx';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@ui/form.tsx';
import { Input } from '@ui/input.tsx';
import { Button } from '@ui/button.tsx';
import {
  getApiAdminsalesHierarchiesIdGetQueryKey,
  useApiAdminsalesHierarchiesIdPatch
} from '@r/api/generated/client.ts';
import type { SalesHierarchySalesHierarchyReadModel } from '@r/api/generated/client.schemas.ts';
import { isOrvalErrorFromPath } from '@r/api/orval-error-helper.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { invalidateSalesHierarchiesQueries } from './hierarchy-utils.ts';

type SalesHierarchy = SalesHierarchySalesHierarchyReadModel;

interface RenameHierarchyDialogProps {
  hierarchy: SalesHierarchy | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Modale de renommage d'une hiérarchie commerciale existante. */
export const RenameHierarchyDialog: FC<RenameHierarchyDialogProps> = ({
  hierarchy,
  isOpen,
  onOpenChange
}) => {
  const { t } = useTranslation();

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="p-10 !rounded-3xl">
        <DialogHeader>
          <DialogTitle className="text-2xl">{t('admin.hierarchy.rename.dialog.title')}</DialogTitle>
          <DialogDescription>{t('admin.hierarchy.rename.dialog.description')}</DialogDescription>
        </DialogHeader>

        {isOpen && hierarchy && (
          <RenameHierarchyForm hierarchy={hierarchy} onSuccess={() => onOpenChange(false)} />
        )}
      </DialogContent>
    </Dialog>
  );
};

interface RenameHierarchyFormProps {
  hierarchy: SalesHierarchy;
  onSuccess: () => void;
}

const RenameHierarchyForm: FC<RenameHierarchyFormProps> = ({ hierarchy, onSuccess }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const showOrvalErrorToast = useOrvalErrorToast();

  const FormSchema = z.object({
    name: z.string().trim().min(1, t('form.fields.required')).max(50)
  });

  type FormValues = z.infer<typeof FormSchema>;

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: { name: hierarchy.name }
  });

  const renameHierarchyMutation = useApiAdminsalesHierarchiesIdPatch({
    mutation: {
      onSuccess: () => {
        toast.success(t('admin.hierarchy.rename.success'));
        invalidateSalesHierarchiesQueries(queryClient);
        queryClient.invalidateQueries({
          queryKey: getApiAdminsalesHierarchiesIdGetQueryKey(String(hierarchy.id))
        });
        onSuccess();
      },
      onError: (error) => {
        if (isOrvalErrorFromPath(error, 'patch', '/admin/sales-hierarchies')) {
          showOrvalErrorToast(error, t('admin.hierarchy.rename.error'));
        } else {
          toast.error(t('admin.hierarchy.rename.error'));
        }
      }
    }
  });

  function onSubmit(data: FormValues) {
    renameHierarchyMutation.mutate({ id: String(hierarchy.id), data: { name: data.name } });
  }

  return (
    <Form {...form} schema={FormSchema}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('admin.hierarchy.form.name.label')}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t('admin.hierarchy.form.name.placeholder')}
                  {...field}
                  error={!!fieldState.error}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button
          rounded="full"
          loading={renameHierarchyMutation.isPending}
          className="w-full"
          type="submit"
        >
          {t('admin.hierarchy.form.submit')}
        </Button>
      </form>
    </Form>
  );
};
